import React, { memo, useCallback } from "react";
import { Box, TextField, Typography } from "@mui/material";
import { COLORS, FONT } from "../../shared/styles";
import { getAppearanceTextErrors } from "./constants";
import PrimaryColorField from "./PrimaryColorField";
import WidgetLogoField from "./WidgetLogoField";

const labelSx = {
  color: COLORS.textSecondary,
  fontFamily: FONT.family,
  fontSize: "12px",
  fontWeight: 400,
  lineHeight: "normal",
  mb: "8px",
};

const errorTextSx = {
  mt: "6px",
  color: COLORS.error,
  fontFamily: FONT.family,
  fontSize: "11px",
  lineHeight: "14px",
};

const inputSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: "40px",
    borderRadius: "8px",
    fontFamily: FONT.family,
    fontSize: "14px",
    color: COLORS.textPrimary,
    boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
    background: COLORS.white,
    "& fieldset": { borderColor: "#B7C2CC" },
    "&:hover fieldset": { borderColor: "#B7C2CC" },
    "&.Mui-focused fieldset": { borderColor: COLORS.primary },
  },
  "& .MuiOutlinedInput-input": { py: 0, px: "12px" },
};

const textareaSx = {
  ...inputSx,
  "& .MuiOutlinedInput-root": {
    ...inputSx["& .MuiOutlinedInput-root"],
    height: "auto",
    minHeight: "80px",
    alignItems: "flex-start",
    py: "10px",
  },
  "& .MuiOutlinedInput-input": {
    py: "0px",
  },
};

const withErrorBorder = (baseSx) => ({
  ...baseSx,
  "& .MuiOutlinedInput-root": {
    ...baseSx["& .MuiOutlinedInput-root"],
    "& fieldset": { borderColor: COLORS.error },
    "&:hover fieldset": { borderColor: COLORS.error },
    "&.Mui-focused fieldset": { borderColor: COLORS.error },
  },
});

const positionBtnSx = (active) => ({
  flex: 1,
  height: "40px",
  borderRadius: "8px",
  border: `1px solid ${COLORS.border}`,
  background: COLORS.white,
  boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
  color: active ? COLORS.textPrimary : COLORS.textSecondary,
  fontFamily: FONT.family,
  fontSize: "14px",
  fontWeight: active ? 500 : 400,
  cursor: "pointer",
});

const PositionField = memo(function PositionField({ value, onChange }) {
  return (
    <Box>
      <Typography sx={labelSx}>Widget Position</Typography>
      <Box sx={{ display: "flex", gap: "8px" }}>
        {["left", "right"].map((pos) => (
          <Box
            key={pos}
            component="button"
            type="button"
            onClick={() => onChange(pos)}
            sx={positionBtnSx(value === pos)}
          >
            {pos.charAt(0).toUpperCase() + pos.slice(1)}
          </Box>
        ))}
      </Box>
    </Box>
  );
});

const TextFieldWithError = memo(function TextFieldWithError({
  label,
  value,
  onChange,
  error,
  multiline = false,
}) {
  const baseSx = multiline ? textareaSx : inputSx;

  return (
    <Box>
      <Typography sx={labelSx}>{label}</Typography>
      <TextField
        value={value}
        multiline={multiline}
        minRows={multiline ? 3 : undefined}
        error={Boolean(error)}
        onChange={(e) => onChange(e.target.value)}
        onBlur={(e) => {
          const trimmed = e.target.value.trim();
          if (trimmed !== value) onChange(trimmed);
        }}
        sx={error ? withErrorBorder(baseSx) : baseSx}
      />
      {error ? <Typography sx={errorTextSx}>{error}</Typography> : null}
    </Box>
  );
});

function AppearanceTab({
  settings,
  onChange,
  logoPreview,
  onUploadLogoClick,
  logoLoading = false,
  hasPendingLogo = false,
  disabled = false,
}) {
  const errors = getAppearanceTextErrors(settings);

  const handleColorChange = useCallback(
    (primaryColor) => onChange({ primaryColor }),
    [onChange],
  );

  const handlePositionChange = useCallback(
    (position) => onChange({ position }),
    [onChange],
  );

  const handleGreetingChange = useCallback(
    (greetingText) => onChange({ greetingText }),
    [onChange],
  );

  const handleGreetingSubtitleChange = useCallback(
    (greetingSubtitle) => onChange({ greetingSubtitle }),
    [onChange],
  );

  const handleWelcomeChange = useCallback(
    (welcomeMessage) => onChange({ welcomeMessage }),
    [onChange],
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <PrimaryColorField value={settings.primaryColor} onChange={handleColorChange} />

      <Box sx={{ width: "100%", height: "1px", background: COLORS.borderLight }} />

      <WidgetLogoField
        logoPreview={logoPreview}
        onUploadClick={onUploadLogoClick}
        loading={logoLoading}
        disabled={disabled}
        hasPendingFile={hasPendingLogo}
      />

      <Box sx={{ width: "100%", height: "1px", background: COLORS.borderLight }} />

      <PositionField value={settings.position} onChange={handlePositionChange} />
      <TextFieldWithError
        label="Greeting Text"
        value={settings.greetingText}
        onChange={handleGreetingChange}
        error={errors.greetingText}
      />
      <TextFieldWithError
        label="Greeting Subtitle"
        value={settings.greetingSubtitle}
        onChange={handleGreetingSubtitleChange}
        error={errors.greetingSubtitle}
      />
      <TextFieldWithError
        label="Welcome Message"
        value={settings.welcomeMessage}
        onChange={handleWelcomeChange}
        error={errors.welcomeMessage}
        multiline
      />
    </Box>
  );
}

export default memo(AppearanceTab);
